import { AnnotaionFields } from '@/components/AnnotationFields';
import { Label } from '@/components/Label';
import { canPolicyActionEdit } from '@/constants/policy';
import { queryClient } from '@/graphql/client';
import { AnnotationTemplate, AnnotationTemplateSelectField } from '@/graphql/codegen/graphql';
import { COUNT_ANNOTATIONS_BY_TEMPLATE_ID } from '@/graphql/queries';
import { request } from '@/graphql/request';
import { useAnnotationTemplates } from '@/hooks/useAnnotationTemplates';
import { useFetchAnnotationHistory } from '@/hooks/useFetchAnnotationHistory';
import { useFetchSceneEntityPermissions } from '@/hooks/useFetchSceneEntityPermissions';
import { useFetchUsers } from '@/hooks/useFetchUsers';
import {
  editAnnotationSetColor,
  editAnnotationSetGroup,
  editAnnotationSetName,
  editAnnotationSetTemplate,
  useExplore,
} from '@/stores/explore';
import {
  startPanoramaWalkthrough,
  startPhotoOverlay,
  stopPanoramaView,
  stopPhotoOverlay,
  useViewer,
} from '@/stores/viewer';
import { createOrderedName, findAnnotationColorFromTemplate } from '@/utils/annotation';
import { AnnotationVersion } from '@/utils/annotationVersion';
import { cn } from '@/utils/classname';
import { getShareLinkToken } from '@/utils/shareLink';
import { User } from '@/utils/user';
import { Input, Select } from '@skand/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColorInput } from './ColorInput';
import { Thumbnail } from './Thumbnail';

interface AnnotationDetailsProps {
  setPresetName: (value: string) => void;
  presetName: string;
  hasJustSwitchedToDetailsTab?: boolean;
}

export const AnnotationDetails = ({
  setPresetName,
  presetName,
  hasJustSwitchedToDetailsTab,
}: AnnotationDetailsProps) => {
  const targetProcess = useViewer(state => state.targetProcess);
  const loadingMutex = useViewer(state => state.loadingMutex);

  const annotationDraft = useExplore(state => state.annotationDraft);
  const annotationGroups = useViewer(state => state.annotationGroups);
  const annotationGroupIdCache = useExplore(state => state.annotationGroupIdCache);
  const annotationTemplateIdCache = useExplore(state => state.annotationTemplateIdCache);

  const [updatedBy, setUpdatedBy] = useState<User['firstName'] | null>(null);
  const [firstAnnotationVersion, setFirstAnnotationVersion] = useState<AnnotationVersion | null>(
    null,
  );

  const fetchUsers = useFetchUsers();
  const { getSceneEntityPermission } = useFetchSceneEntityPermissions();
  const { templates } = useAnnotationTemplates();

  // Change annotation template
  const handleChangeTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find(item => item?.id === templateId);
      if (!template) return;

      editAnnotationSetTemplate(template);
      const color = findAnnotationColorFromTemplate(annotationDraft, template);
      if (color) editAnnotationSetColor(color);

      // Store template id
      useExplore.setState({ annotationTemplateIdCache: templateId });
    },
    [annotationDraft, templates],
  );

  const annotation = useMemo(() => {
    for (const group of annotationGroups) {
      for (const annotation of group.annotations) {
        if (annotation.id === annotationDraft?.annotationId) {
          return annotation;
        }
      }
    }
    return undefined;
  }, [annotationDraft, annotationGroups]);

  const fetchAnnotationHistory = useFetchAnnotationHistory(annotation?.id ?? null);
  const groupPermission = getSceneEntityPermission(annotation?.group.sceneEntityId ?? null);

  const canEdit = useMemo(() => {
    if (getShareLinkToken() !== null) return false;
    if (annotationDraft?.mode === 'create') return true;
    if (annotationDraft?.mode === 'preview') return false;
    return canPolicyActionEdit(groupPermission);
  }, [annotationDraft, groupPermission]);

  const templateOptions = useMemo(() => {
    const options =
      templates.map(template => {
        return {
          key: template?.id || '',
          name: template?.name || '',
        };
      }) ?? [];

    return options;
  }, [templates]);

  // Annotation group options
  const groupOptions = useMemo(() => {
    const options = [];
    for (const group of annotationGroups) {
      const access = getSceneEntityPermission(group.sceneEntityId);
      if (annotationDraft?.mode === 'update' || canPolicyActionEdit(access)) {
        options.push({
          key: group.id,
          name: group.name,
        });
      }
    }
    return options;
  }, [annotationDraft?.mode, annotationGroups, getSceneEntityPermission]);

  useEffect(() => {
    fetchAnnotationHistory().then(result => {
      setFirstAnnotationVersion(result[0]);
    });
  }, [fetchAnnotationHistory]);

  useEffect(() => {
    fetchUsers().then(users => {
      const user = users.find(user => user.id === annotation?.updatedBy);
      if (user) {
        setUpdatedBy(`${user.firstName} ${user.lastName}`);
      } else {
        setUpdatedBy('System');
      }
    });
  }, [annotation?.updatedBy, fetchUsers]);

  useEffect(() => {
    if (annotationDraft?.template) {
      queryClient
        .fetchQuery({
          queryFn: () =>
            request(COUNT_ANNOTATIONS_BY_TEMPLATE_ID, {
              templateId: annotationDraft?.template?.id as string,
              filterDeleted: false,
            }),
        })
        .then(response =>
          setPresetName(
            createOrderedName(
              annotationDraft.template as AnnotationTemplate,
              response.countActiveAnnotationsByTemplateId as number,
            ),
          ),
        );
    } else {
      setPresetName('');
    }
  }, [annotationDraft, setPresetName]);
  const isPresetNameEnabled = annotationDraft?.mode === 'create';

  // Set previously selected annotation group and template as default options if it's create mode
  useEffect(() => {
    if (annotationDraft?.mode === 'create') {
      if (!annotationDraft.templateId && annotationTemplateIdCache) {
        handleChangeTemplate(annotationTemplateIdCache);
      }

      if (!annotationDraft.groupId && annotationGroupIdCache) {
        editAnnotationSetGroup(annotationGroupIdCache);
      }
    }
  }, [
    annotationDraft,
    annotationDraft?.mode,
    annotationGroupIdCache,
    annotationTemplateIdCache,
    handleChangeTemplate,
  ]);

  const getColorField = () => {
    const templateField = annotationDraft?.template?.fields?.find(
      item => item?.name === annotationDraft?.template?.colorFromField,
    );
    const annotationField = annotationDraft?.fields?.find(
      field => field?.fieldId === templateField?.id,
    );
    if (!templateField) return annotationDraft?.color?.toUpperCase();
    else {
      const options = (templateField as AnnotationTemplateSelectField).options;
      const selected = options?.find(item => item?.id === annotationField?.optionId);
      return selected?.color?.toUpperCase();
    }
  };

  // Handle viewing the associated 2D image (if it exists)
  const handleViewAssociatedImage = async () => {
    if (annotation?.photo && !loadingMutex) {
      stopPhotoOverlay();
      stopPanoramaView();

      if (annotation.photo.type === 'photo2D') {
        await startPhotoOverlay(annotation.photo, !annotation.sketch3D, false, false);
      } else {
        await startPanoramaWalkthrough(annotation.photo);
      }
    }
  };

  const annotationNamePlaceholder = isPresetNameEnabled ? presetName : undefined;

  return (
    <div className={cn('min-h-10vh', 'max-h-60vh', 'overflow-auto')}>
      <div className="flex-1 overflow-auto">
        {targetProcess?.kind === 'annotation' &&
          (annotationDraft?.mode === 'create' ||
            targetProcess.annotationIds.has(annotationDraft?.annotationId as string)) && (
            <Label
              ellipsis={false}
              labelTitle={`This annotation is associated with the current active process (${targetProcess.id}).`}
              labelType="info"
              textLength={150}
            />
          )}

        <Input
          disabled={!canEdit}
          hint={annotationDraft?.name ? undefined : 'Required'}
          label="Name"
          onChange={editAnnotationSetName}
          placeholder={annotationNamePlaceholder}
          value={annotationDraft?.name ?? ''}
          warn={hasJustSwitchedToDetailsTab && !annotationDraft?.name && !annotationNamePlaceholder}
        />

        {annotationDraft?.mode !== 'create' && (
          <div className={cn('flex flex-col', 'p-x-3 p-y-2 bg-neutral-200', 'mt-3', 'b-rounded')}>
            <div className={cn('flex flex-row')}>
              <p className="color-neutral-500 typo-text-s">
                Created By:
                <span className="mx-1 color-neutral-600 typo-text-s">
                  {firstAnnotationVersion?.createdBy?.firstName ?? 'System'}{' '}
                  {firstAnnotationVersion?.createdBy?.lastName ?? ''}
                </span>
              </p>
              <p className="color-neutral-500 typo-text-s">
                on
                <span className="ml-1 color-neutral-600 typo-text-s">
                  {firstAnnotationVersion?.createdAt.toLocaleDateString()}
                </span>
              </p>
            </div>
            <div className={cn('flex flex-row')}>
              <p className="color-neutral-500 typo-text-s">
                Updated By:
                <span className="mx-1 color-neutral-600 typo-text-s">{updatedBy}</span>{' '}
              </p>
              <p className="color-neutral-500 typo-text-s">
                on
                <span className="ml-1 color-neutral-600 typo-text-s">
                  {annotation?.updatedAt.toLocaleDateString()}
                </span>
              </p>
            </div>
          </div>
        )}

        <div className="mt-3">
          <Select
            disabled={annotationDraft?.mode !== 'create' || !canEdit}
            hint={annotationDraft?.templateId ? undefined : 'Required'}
            label="Template"
            onChange={handleChangeTemplate}
            options={templateOptions}
            style={{ display: 'block' }}
            value={annotationDraft?.templateId}
            warn={hasJustSwitchedToDetailsTab && !annotationDraft?.templateId}
            width="full"
          />
        </div>
        <div className="mt-3">
          <Select
            disabled={!canEdit || groupOptions.length === 0}
            fallbackStrategy="bestFit"
            hint={annotationDraft?.groupId ? undefined : 'Required'}
            label="Annotation group"
            onChange={id => {
              editAnnotationSetGroup(id);
              useExplore.setState({ annotationGroupIdCache: id });
            }}
            options={groupOptions}
            style={{ display: 'block' }}
            value={annotationDraft?.groupId}
            warn={hasJustSwitchedToDetailsTab && !annotationDraft?.groupId}
            width="full"
          />
        </div>

        {annotationDraft?.annotation2d?.imageFileId && (
          <div className="mt-3">
            <p className="color-neutral-800 typo-text-s">Associated 2D image</p>
            <div className="cursor-pointer" onClick={handleViewAssociatedImage}>
              <Thumbnail fileId={annotationDraft.annotation2d.imageFileId} />
            </div>
          </div>
        )}

        <div className="mt-3">
          <ColorInput
            disabled={!!annotationDraft?.template?.colorFromField || !canEdit}
            hint={
              annotationDraft?.template?.colorFromField
                ? `The colour of this annotation is determined by the ${annotationDraft.template.colorFromField} field.`
                : ''
            }
            onChange={editAnnotationSetColor}
            value={getColorField()}
          />
        </div>
        {annotationDraft?.template && <AnnotaionFields disabled={!canEdit} />}
      </div>
    </div>
  );
};
